import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import LoadingSpinner from '../components/LoadingSpinner';

export default function BookingConfirmed() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get(`/api/bookings/track?reference=${reference}`)
      .then((res) => setBooking(res.data))
      .catch(() => setError('Booking not found. Please check your reference number.'))
      .finally(() => setLoading(false));
  }, [reference]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-6 text-center">
        <p className="text-red-600 text-lg mb-6">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-brand-green text-white rounded-xl font-semibold hover:bg-brand-ocean transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const details = [
    { label: 'Name',       value: booking.customer_name },
    { label: 'Pickup',     value: booking.pickup_location },
    { label: 'Drop-off',   value: booking.dropoff_location },
    { label: 'Date',       value: formatDate(booking.pickup_date) },
    { label: 'Time',       value: booking.pickup_time },
    { label: 'Passengers', value: `${booking.passengers} ${booking.passengers === 1 ? 'passenger' : 'passengers'}` },
  ];

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 border border-brand-sand">

        {/* Success icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-brand-ocean"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h1 className="font-serif text-3xl font-bold text-brand-green text-center mb-2">
          Booking Received
        </h1>
        <p className="text-brand-ocean text-center text-sm mb-7">
          Our team will contact you within 2 hours to confirm your transfer.
        </p>

        {/* Reference number */}
        <div className="bg-brand-sand rounded-xl px-6 py-4 mb-7 border-l-4 border-brand-gold text-center">
          <p className="text-brand-ocean text-xs font-bold uppercase tracking-widest mb-1">
            Booking Reference
          </p>
          <p className="font-serif text-3xl font-bold text-brand-green tracking-wider">
            {booking.booking_reference}
          </p>
          <p className="text-brand-ocean text-xs mt-1">Save this number to track your booking</p>
        </div>

        {/* Summary */}
        <div className="space-y-3 mb-7">
          {details.map(({ label, value }) => (
            <div key={label} className="flex gap-4 items-start">
              <span className="text-brand-ocean text-sm font-semibold w-24 shrink-0">
                {label}
              </span>
              <span className="text-brand-green text-sm">{value}</span>
            </div>
          ))}
          <div className="flex gap-4 items-start">
            <span className="text-brand-ocean text-sm font-semibold w-24 shrink-0">Status</span>
            <span className="text-amber-700 text-sm font-semibold capitalize">
              {booking.status}
            </span>
          </div>
        </div>

        {/* WhatsApp CTA */}
        <a
          href="https://wa.me/255779987604"
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 bg-brand-green text-white rounded-xl hover:bg-brand-ocean transition-colors font-semibold mb-3"
        >
          <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Contact via WhatsApp
        </a>

        <button
          onClick={() => navigate('/')}
          className="w-full py-3 border-2 border-brand-sand text-brand-ocean rounded-xl hover:bg-brand-sand transition-colors font-semibold"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}
